import { AfterViewChecked, Component, computed, ElementRef, HostBinding, inject, input, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { CuiTooltipDirective } from '@idealink-material/ui-core';
import { CommonModule } from '@angular/common';
import { LayoutService } from '@/layout/service/layout.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[app-menuitem]',
    imports: [CommonModule, RouterModule, CuiTooltipDirective],
    template: `
        <ng-container>
            @if (root) {
                <div class="layout-menuitem-root-text">
                    {{ item.label }}
                </div>
            }
            @if (!item.routerLink || item.items) {
                <a
                    [attr.href]="item.url"
                    (click)="itemClick($event)"
                    (mouseenter)="onMouseEnter()"
                    [class.active-route]="isActive()"
                    [ngClass]="item.class"
                    [attr.target]="item.target"
                    tabindex="0"
                    [cuiTooltip]="item.label"
                    [cuiTooltipDisabled]="!(isSlim() && root && !active)"
                >
                    @if (item.icon?.startsWith('ms:')) {
                        <i class="material-symbols-outlined layout-menuitem-icon">{{ item.icon.slice(3) }}</i>
                    } @else {
                        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
                    }
                    <span class="layout-menuitem-text shimmer-text" [class.is-loading]="loading()" [attr.data-text]="item.label">
                        {{ item.label }}
                    </span>
                    @if (item.items) {
                        <i class="material-symbols-outlined layout-submenu-toggler">keyboard_arrow_down</i>
                    }
                </a>
            }
            @if (item.routerLink && !item.items) {
                <a
                    (click)="itemClick($event)"
                    (mouseenter)="onMouseEnter()"
                    [ngClass]="item.class"
                    [routerLink]="item.routerLink"
                    routerLinkActive="active-route"
                    [routerLinkActiveOptions]="
                        item.routerLinkActiveOptions || {
                            paths: 'subset',
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            fragment: 'ignored'
                        }
                    "
                    [fragment]="item.fragment"
                    [queryParamsHandling]="item.queryParamsHandling"
                    [preserveFragment]="item.preserveFragment"
                    [skipLocationChange]="item.skipLocationChange"
                    [replaceUrl]="item.replaceUrl"
                    [state]="item.state"
                    [queryParams]="item.queryParams"
                    [attr.target]="item.target"
                    tabindex="0"
                    [cuiTooltip]="item.label"
                    [cuiTooltipDisabled]="!(isSlim() && root)"
                >
                    @if (item.icon?.startsWith('ms:')) {
                        <i class="material-symbols-outlined layout-menuitem-icon">{{ item.icon.slice(3) }}</i>
                    } @else {
                        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
                    }
                    <span class="layout-menuitem-text shimmer-text" [class.is-loading]="loading()" [attr.data-text]="item.label">
                        {{ item.label }}
                    </span>
                    @if (item.items) {
                        <i class="material-symbols-outlined layout-submenu-toggler">keyboard_arrow_down</i>
                    }
                </a>
            }

            @if (item.items) {
                <ul #submenu [@children]="submenuAnimation" (@children.done)="onSubmenuAnimated($event)" class="no-scrollbar">
                    @for (child of item.items; track $index) {
                        <li app-menuitem [item]="child" [index]="$index" [parentKey]="key" [class]="child['badgeClass']"></li>
                    }
                </ul>
            }
        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [
        trigger('children', [
            state(
                'collapsed',
                style({
                    height: '0'
                })
            ),
            state(
                'expanded',
                style({
                    height: '*'
                })
            ),
            state(
                'hidden',
                style({
                    display: 'none'
                })
            ),
            state(
                'visible',
                style({
                    display: 'block'
                })
            ),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class AppMenuitem implements OnInit, OnDestroy, AfterViewChecked {
    loading = input<boolean>(false);
    @Input() item: any;
    @Input() index!: number;
    @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;
    @Input() parentKey!: string;

    @ViewChild('submenu') submenu!: ElementRef;
    active = false;
    menuSourceSubscription: Subscription;
    menuResetSubscription: Subscription;
    key: string = '';
    isSlim = computed(() => this.layoutService.isSlim());
    isSlimPlus = computed(() => this.layoutService.isSlimPlus());
    isHorizontal = computed(() => this.layoutService.isHorizontal());
    router = inject(Router);
    // We listen to NavigationEnd events to update the signal
    currentUrl = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.urlAfterRedirects)
        ),
        { initialValue: this.router.url }
    );
    // 2. Compute if this specific item is active
    isActive = computed(() => {
        const link = this.item.routerLink;
        if (!link) return false;
        const path = Array.isArray(link) ? link.join('/') : link;
        // Check if the current URL starts with our path
        return this.currentUrl().startsWith(path);
    });

    constructor(public layoutService: LayoutService) {
        this.menuSourceSubscription = this.layoutService.menuSource$.subscribe((value) => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = value.key === this.key || value.key.startsWith(this.key + '-');
                } else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                }
            });
        });

        this.menuResetSubscription = this.layoutService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((_) => {
            if (this.isSlimPlus() || this.isSlim() || this.isHorizontal()) {
                this.active = false;
            } else {
                if (this.item.routerLink) {
                    this.updateActiveStateFromRoute();
                }
            }
        });
    }

    @HostBinding('class.active-menuitem')
    get activeClass() {
        return this.active;
    }

    // get isMobile() {
    //     return this.layoutService.isMobile();
    // }

    get submenuAnimation() {
        if (this.layoutService.isDesktop() && (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())) {
            return this.active ? 'visible' : 'hidden';
        } else return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
    }

    get isDesktop() {
        return this.layoutService.isDesktop();
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        if (!(this.isSlimPlus() || this.isSlim() || this.isHorizontal()) && this.item.routerLink) {
            this.updateActiveStateFromRoute();
        }
    }

    ngAfterViewChecked() {
        if (this.root && this.active && this.isDesktop && (this.isHorizontal() || this.isSlim() || this.isSlimPlus())) {
            this.calculatePosition(this.submenu?.nativeElement, this.submenu?.nativeElement.parentElement);
        }
    }

    updateActiveStateFromRoute() {
        let activeRoute = this.router.isActive(this.item.routerLink[0], {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored'
        });

        if (activeRoute) {
            this.layoutService.onMenuStateChange({
                key: this.key,
                routeEvent: true
            });
        }
    }

    onSubmenuAnimated(event: AnimationEvent) {
        if (event.toState === 'visible' && this.isDesktop && (this.isHorizontal() || this.isSlim() || this.isSlimPlus())) {
            const el = <HTMLUListElement>event.element;
            const elParent = <HTMLUListElement>el.parentElement;
            this.calculatePosition(el, elParent);
        }
    }

    calculatePosition(overlay: HTMLElement, target: HTMLElement) {
        if (overlay) {
            const { top } = target.getBoundingClientRect();
            const vHeight = window.innerHeight;
            const oHeight = overlay.offsetHeight;
            const topbarEl = document.querySelector('.layout-topbar') as HTMLElement;
            const topbarHeight = topbarEl?.offsetHeight || 0;
            // reset
            overlay.style.top = '';
            overlay.style.left = '';

            if (this.layoutService.isSlim() || this.layoutService.isSlimPlus()) {
                const topOffset = top - topbarHeight;
                const height = topOffset + oHeight + topbarHeight;
                overlay.style.top = vHeight < height ? `${topOffset - (height - vHeight)}px` : `${topOffset}px`;
            }
        }
    }

    itemClick(event: MouseEvent) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // navigate with hover
        if ((this.root && this.isSlim()) || this.isHorizontal() || this.isSlimPlus()) {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                menuHoverActive: !val.menuHoverActive
            }));
        }

        // execute command
        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // add tab
        if ((event.metaKey || event.ctrlKey) && this.item.routerLink && (!this.item.data || !this.item.data.fullPage)) {
            this.layoutService.onTabOpen(this.item);
            event.preventDefault();
        }

        // toggle active state
        if (this.item.items) {
            this.active = !this.active;

            if (this.root && this.active && (this.isSlim() || this.isHorizontal() || this.isSlimPlus())) {
                this.layoutService.onOverlaySubmenuOpen();
            }
        } else {
            if (this.layoutService.isMobile()) {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    staticMenuMobileActive: false
                }));
            }

            if (this.isSlim() || this.isHorizontal() || this.isSlimPlus()) {
                this.layoutService.reset();
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    menuHoverActive: false
                }));
            }
        }

        this.layoutService.onMenuStateChange({ key: this.key });
    }

    // 1. Create a signal of the current URL

    onMouseEnter() {
        // activate item on hover
        if (this.root && (this.isSlim() || this.isHorizontal() || this.isSlimPlus()) && this.layoutService.isDesktop()) {
            if (this.layoutService.layoutState().menuHoverActive) {
                this.active = true;
                this.layoutService.onMenuStateChange({ key: this.key });
            }
        }
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }
}
